import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useInternalDeliveryFacilityLockGetFacilityLocks } from 'api/internal-delivery-facility-lock/internal-delivery-facility-lock';
import { InternalDeliveryFacilityLockDTO } from 'api/model';

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

interface Options {
  /**
   * Till-anläggning och starttid förfrågan redan har (vid redigering). Ligger förfrågan kvar på
   * samma anläggning och samma dag räknas den inte som spärrad — backenden nekar bara när
   * till-anläggningen eller startdagen byts till en låst.
   */
  originalToCompanyId?: number | null;
  originalTargetStartTime?: Date | null;
}

/**
 * Är vald till-anläggning låst i annonsöverblicken på förfrågans startdag? Samma regel som
 * AssignmentRequestLogic.EnsureToCompanyNotLocked, så att spara-knappen kan stängas av i förväg
 * i stället för att backenden nekar.
 */
export const useToCompanyLock = (
  toCompanyId: number | null | undefined,
  targetStartTime: Date | null | undefined,
  { originalToCompanyId, originalTargetStartTime }: Options = {}
): {
  isLocked: boolean;
  lock?: InternalDeliveryFacilityLockDTO;
} => {
  // Dagens början som nyckel, så att varje minutändring i tidsfältet inte blir en ny fråga.
  const dayKey = targetStartTime ? startOfDay(targetStartTime).getTime() : 0;
  const locks = useInternalDeliveryFacilityLockGetFacilityLocks(
    dayKey ? { date: new Date(dayKey) } : undefined,
    { query: { staleTime: 1000 * 30, enabled: !!toCompanyId } }
  );

  const originalDayKey = originalTargetStartTime
    ? startOfDay(originalTargetStartTime).getTime()
    : undefined;

  return useMemo(() => {
    const unchanged =
      toCompanyId === originalToCompanyId && dayKey === originalDayKey;
    if (!toCompanyId || unchanged) {
      return { isLocked: false };
    }
    const lock = locks.data?.find((l) => l.companyId === toCompanyId);
    return { isLocked: !!lock, lock };
  }, [dayKey, locks.data, originalDayKey, originalToCompanyId, toCompanyId]);
};

const Warning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.colors.foreground.error};
  font-weight: 500;
  white-space: nowrap;
`;

interface Props {
  lock?: InternalDeliveryFacilityLockDTO;
}

/** Varningen bredvid till-anläggningens dropdown. Renderar inget när anläggningen inte är låst. */
const FacilityLockWarning: FC<Props> = ({ lock }) => {
  if (!lock) return null;

  const time = lock.lockedAt.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Warning
      title={`Låst av ${lock.lockedByName ?? 'okänd'} kl ${time}. Låset släpper vid midnatt.`}
    >
      <FontAwesomeIcon icon={faLock} />
      Låst för annonsförfrågningar i dag
    </Warning>
  );
};

export default FacilityLockWarning;
